import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Button,
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Paper 
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useHistory } from 'react-router-dom';

const ProductDetailSentinel1 = () => {
  const history = useHistory();
  const [mapType, setMapType] = useState('satellite'); // 'street' or 'satellite'

  return (
    <Box sx={{ maxWidth: '1200px', mx: 'auto', p: 2 }}>
      {/* Back Navigation */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          sx={{
            color: '#4287f5',
            textTransform: 'none',
            fontWeight: 500
          }}
          onClick={() => history.push('/products')}
        >
          Products &gt; Sentinel-1
        </Button>
      </Box>

      {/* Product Description */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" sx={{ mb: 1, fontWeight: 500 }}>
          Product Description
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.primary', lineHeight: 1.6 }}>
          Sentinel-1 is a C-band Synthetic Aperture Radar (SAR) mission that forms part of the Copernicus Earth Observation program. The mission consists of two satellites, Sentinel-1A and Sentinel-1B, operating in a phased orbit to provide all-weather, day-and-night imagery capabilities.
          <br /><br />
          Unlike optical sensors, SAR technology enables continuous monitoring regardless of weather conditions, cloud cover, or daylight availability. This makes Sentinel-1 particularly valuable for applications requiring consistent temporal coverage, such as maritime surveillance, flood monitoring, oil spill detection, sea ice mapping, and land deformation monitoring.
          <br /><br />
          Sentinel-1 operates in C-band (5.405 GHz) and offers multiple acquisition modes including Interferometric Wide Swath (IW), Extra Wide Swath (EW), Strip Map (SM), and Wave (WV) modes, providing flexibility for various applications. The mission delivers high-quality SAR data with excellent radiometric and geometric accuracy, supporting both scientific research and operational services.
        </Typography>
      </Box>

      {/* Specifications Table */}
      <TableContainer component={Paper} sx={{ mb: 4, border: '1px solid #d0d7de', boxShadow: 'none' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f0f4fa' }}>
              <TableCell align="center" sx={{ fontWeight: 600, color: '#1976d2' }}>Geographical Coverage</TableCell>
              <TableCell align="center" sx={{ fontWeight: 600, color: '#1976d2' }}>Image Resolution</TableCell>
              <TableCell align="center" sx={{ fontWeight: 600, color: '#1976d2' }}>Source</TableCell>
              <TableCell align="center" sx={{ fontWeight: 600, color: '#1976d2' }}>Revisit Frequency</TableCell>
              <TableCell align="center" sx={{ fontWeight: 600, color: '#1976d2' }}>Data Format</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow hover>
              <TableCell align="center">Global*</TableCell>
              <TableCell align="center">5 m x 20 m (IW mode)</TableCell>
              <TableCell align="center">Sentinel-1</TableCell>
              <TableCell align="center">6-day cycle</TableCell>
              <TableCell align="center">GeoTIFF</TableCell>
            </TableRow>
            <TableRow>
              <TableCell colSpan={5} sx={{ fontStyle: 'italic', fontSize: '0.85rem', color: 'gray', py: 1 }}>
                *Global coverage available. AOI outside India requires additional processing time.
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>

        <Table sx={{ mt: 4 }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f0f4fa' }}>
              <TableCell sx={{ fontWeight: 600, color: '#1976d2' }}>Acquisition Mode</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#1976d2' }}>Resolution</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#1976d2' }}>Swath Width</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#1976d2' }}>Description</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow hover>
              <TableCell>Interferometric Wide Swath (IW)</TableCell>
              <TableCell>5 m x 20 m</TableCell>
              <TableCell>250 km</TableCell>
              <TableCell>Primary mode for land applications, providing wide coverage with good resolution.</TableCell>
            </TableRow>
            <TableRow hover>
              <TableCell>Extra Wide Swath (EW)</TableCell>
              <TableCell>20 m x 40 m</TableCell>
              <TableCell>400 km</TableCell>
              <TableCell>Optimized for maritime and sea ice monitoring with very wide coverage.</TableCell>
            </TableRow>
            <TableRow hover>
              <TableCell>Strip Map (SM)</TableCell>
              <TableCell>5 m x 5 m</TableCell>
              <TableCell>80 km</TableCell>
              <TableCell>High-resolution mode for detailed analysis of specific areas.</TableCell>
            </TableRow>
            <TableRow hover>
              <TableCell>Wave (WV)</TableCell>
              <TableCell>5 m x 5 m</TableCell>
              <TableCell>20 km x 20 km</TableCell>
              <TableCell>Specialized mode for ocean wave spectra measurements.</TableCell>
            </TableRow>
          </TableBody>
        </Table>

        <Table sx={{ mt: 4 }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f0f4fa' }}>
              <TableCell sx={{ fontWeight: 600, color: '#1976d2' }}>Polarization</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#1976d2' }}>Description</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow hover>
              <TableCell>VV (Vertical-Vertical)</TableCell>
              <TableCell>Transmit and receive in vertical polarization. Good for ocean and sea ice applications.</TableCell>
            </TableRow>
            <TableRow hover>
              <TableCell>HH (Horizontal-Horizontal)</TableCell>
              <TableCell>Transmit and receive in horizontal polarization. Better for land applications and vegetation.</TableCell>
            </TableRow>
            <TableRow hover>
              <TableCell>VH (Vertical-Horizontal)</TableCell>
              <TableCell>Cross-polarization mode. Useful for agricultural monitoring and land cover classification.</TableCell>
            </TableRow>
            <TableRow hover>
              <TableCell>HV (Horizontal-Vertical)</TableCell>
              <TableCell>Cross-polarization mode. Complementary to VH for enhanced land feature discrimination.</TableCell>
            </TableRow>
            <TableRow hover>
              <TableCell>Dual Polarization (VV+VH or HH+HV)</TableCell>
              <TableCell>Combines co-polarization and cross-polarization for comprehensive analysis.</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* Order Button */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
        <Button
          variant="contained"
          sx={{
            bgcolor: '#1976d2',
            color: 'white',
            py: 1,
            px: 4,
            '&:hover': {
              bgcolor: '#1565c0'
            }
          }}
        >
          Order Imagery
        </Button>
      </Box>
    </Box>
  );
};

export default ProductDetailSentinel1;

